import { useLogger } from '@shared/logger';
import { usePermission } from '@shared/permissions';

import type { AppMenu, AppMenuConfig, MenuItem, MenuItemConfig } from '../../types';

import { iconMapper, mapper } from './utils';

function mapMenuItem(item: MenuItemConfig): Pick<MenuItem, 'label' | 'icon' | 'link'> {
  return {
    label: item.label,
    link: item.url,
    icon: iconMapper[item.iconName ?? ''] ?? null,
  };
}

function mapSecondaryMenu(menuItems?: MenuItemConfig[]) {
  return menuItems?.map(mapMenuItem);
}

function mapSubMenu(subMenuItems?: MenuItemConfig[]) {
  return subMenuItems?.map((subMenuItem) => ({
    ...mapMenuItem(subMenuItem),
    secondaryMenu: mapSecondaryMenu(subMenuItem.secondaryMenu),
  }));
}

function mapMenuItems(items?: MenuItemConfig[]) {
  return (
    items?.map((item) => ({
      ...mapMenuItem(item),
      subMenu: mapSubMenu(item.subMenu),
      secondaryMenu: mapSecondaryMenu(item.secondaryMenu),
    })) ?? []
  );
}

function mapAppMenuData(appMenuConfig: AppMenuConfig[] = []): AppMenu[] {
  return appMenuConfig.map((menu) => ({
    section: menu.section,
    items: mapMenuItems(menu.items ?? []),
  }));
}

function useAppMenu({ appShell }: { appShell: AppMenuConfig[] }) {
  const { permissions } = usePermission();
  const logger = useLogger();
  const sideNavMenuItems: AppMenuConfig[] = mapper(appShell, permissions);

  const appMenu: AppMenu[] = mapAppMenuData(sideNavMenuItems);
  logger.debug({ appMenu });

  return { appMenu };
}

export { useAppMenu };
export type { AppMenu, MenuItem };
